import React, { useState, useEffect, useRef } from 'react';
import { X, Camera, Zap, ChevronDown, Save, Store, Sparkles } from 'lucide-react';
import { useLang } from '../../core/lang.jsx';
import LangKeys from '../../core/locales/keys';
import itemService from '../../services/itemService';
import UnitPickerModal from './UnitPickerModal';
import GstPickerModal from './GstPickerModal';
import CategoryPickerModal, { getCategoryDetails } from './CategoryPickerModal';
import PhotoGuidelinesAndVideoCard from './PhotoGuidelinesAndVideoCard';
import DescriptionToolbarTextarea from './DescriptionToolbarTextarea';
import ImageCropperModal from './ImageCropperModal';
import { compressImage } from '../../core/imageUtils';

const AddServiceModal = ({ isOpen, onClose, item = null, onSuccess }) => {
  const { t } = useLang();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showGstDetails, setShowGstDetails] = useState(false);
  const [showUnitPicker, setShowUnitPicker] = useState(false);
  const [showGstPicker, setShowGstPicker] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    item_type: 'service',
    unit: 'NOS',
    sale_price: '',
    discount_price: '',
    tax_included: true,
    is_public: true,
    category: 'GeneralService',
    description: '',
    hsn_sac_code: '',
    gst_rate: '',
    photo_url: null,
    image_file: null
  });

  useEffect(() => {
    if (item) {
      setFormData({
        ...item,
        name: item.name || '',
        unit: item.unit || 'NOS',
        sale_price: item.sale_price !== null && item.sale_price !== undefined ? String(item.sale_price) : '',
        discount_price: item.discount_price ? String(item.discount_price) : '',
        hsn_sac_code: item.hsn_sac_code || '',
        gst_rate: item.gst_rate || '',
        is_public: item.is_public !== 0 && item.is_public !== false && item.is_public !== '0',
        category: item.category || 'GeneralService',
        description: item.description || '',
        tax_included: item.tax_included !== 0 && item.tax_included !== false && item.tax_included !== '0',
        photo_url: item.photo_url || null,
        image_file: null
      });
      setShowGstDetails(!!(item.gst_rate || item.hsn_sac_code));
    } else {
      setFormData({
        name: '',
        item_type: 'service',
        unit: 'NOS',
        sale_price: '',
        discount_price: '',
        tax_included: true,
        is_public: true,
        category: 'GeneralService',
        description: '',
        hsn_sac_code: '',
        gst_rate: '',
        photo_url: null,
        image_file: null
      });
      setShowGstDetails(false);
    }
  }, [item, isOpen]);

  if (!isOpen) return null;

  const handlePhotoClick = () => fileInputRef.current.click();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setRawImageSrc(event.target.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    }
  };

  const handleCropComplete = (croppedFile, previewUrl) => {
    setFormData((prev) => ({
      ...prev,
      photo_url: previewUrl,
      image_file: croppedFile
    }));
  };

  const selectedCategoryInfo = getCategoryDetails(formData.category, true, t);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert(t(LangKeys.please_enter_service_name));
      return;
    }
    setLoading(true);

    try {
      const response = item 
        ? await itemService.updateItem(item.id, formData) 
        : await itemService.createItem(formData);

      if (response === true) {
        onSuccess();
        onClose();
      } else {
        alert("Failed to save: " + (response?.message || "Unknown server error"));
      }
    } catch (err) {
      console.error("Service Save error:", err);
      alert("An error occurred while saving the service.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTitleRow}>
            <h2 style={styles.title}>{item ? t(LangKeys.edit_service) : t(LangKeys.add_service)}</h2>
            <button style={styles.closeBtn} onClick={onClose}><X size={20} /></button>
          </div>
        </div>

        {/* Body */}
        <div style={styles.body}>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleFileChange} />

          {/* SERVICE NAME & PHOTO */}
          <div style={styles.sectionCard}>
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t(LangKeys.service_name)}</label>
                <input
                  style={styles.input}
                  placeholder={t(LangKeys.enter_service_name_here)}
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div style={styles.photoBox} onClick={handlePhotoClick}>
                <div style={styles.photoPreview}>
                  {formData.photo_url ? (
                    <img src={formData.photo_url} alt="" style={styles.img} />
                  ) : (
                    <Camera size={24} color="#94A3B8" />
                  )}
                </div>
                <span style={styles.photoText}>{t(LangKeys.photo)}</span>
              </div>
            </div>
          </div>

          {/* PRICE & UNIT SECTION */}
          <div style={styles.sectionCard}>
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t(LangKeys.service_price)}</label>
                <div style={styles.priceInputRow}>
                  <span style={{ color: '#16A34A', fontWeight: 'bold' }}>₹</span>
                  <input
                    type="number"
                    style={styles.priceInput}
                    placeholder="0"
                    value={formData.sale_price}
                    onChange={e => setFormData({ ...formData, sale_price: e.target.value })}
                  />
                </div>
              </div>
              <div style={styles.inputGroup} onClick={() => setShowUnitPicker(true)}>
                <label style={styles.label}>{t(LangKeys.primary_unit)}</label>
                <div style={styles.selectBox}>
                  <span>{formData.unit}</span>
                  <ChevronDown size={18} color="#94A3B8" />
                </div>
              </div>
            </div>
            <div style={styles.taxToggle}>
              <span>{t(LangKeys.tax_included_in_price)}</span>
              <div
                style={{ ...styles.switch, backgroundColor: formData.tax_included ? '#9333EA' : '#E2E8F0' }}
                onClick={() => setFormData({ ...formData, tax_included: !formData.tax_included })}
              >
                <div style={{ ...styles.switchDot, left: formData.tax_included ? '22px' : '4px' }} />
              </div>
            </div>
          </div>

          {/* SAC & GST DETAILS */}
          {!showGstDetails ? (
            <button style={styles.gstToggleBtn} onClick={() => setShowGstDetails(true)}>
              + {t(LangKeys.add_sac_and_gst_details)}
            </button>
          ) : (
            <div style={styles.sectionCard}>
              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>{t(LangKeys.sac_code)}</label>
                  <input
                    style={styles.input}
                    placeholder={t(LangKeys.enter_sac)}
                    value={formData.hsn_sac_code}
                    onChange={e => setFormData({ ...formData, hsn_sac_code: e.target.value })}
                  />
                </div>
                <div style={styles.inputGroup} onClick={() => setShowGstPicker(true)}>
                  <label style={styles.label}>{t(LangKeys.gst_rate_percent)}</label>
                  <div style={styles.selectBox}>
                    <span style={{ color: formData.gst_rate ? '#1E293B' : '#94A3B8' }}>
                      {formData.gst_rate || t(LangKeys.enter_gst_rate)}
                    </span>
                    <ChevronDown size={18} color="#94A3B8" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ONLINE MARKETPLACE TOGGLE & SERVICE CATEGORY (AT BOTTOM LIKE MOBILE APP) */}
          <div style={styles.marketplaceCard}>
            <div style={styles.marketplaceHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Store size={18} color="#9333EA" />
                <span style={styles.marketplaceTitle}>
                  {t(LangKeys.item_marketplace_title) || 'Online Marketplace'}
                </span>
              </div>
              <div
                style={{
                  ...styles.switch,
                  backgroundColor: formData.is_public ? '#16A34A' : '#E2E8F0'
                }}
                onClick={() => setFormData({ ...formData, is_public: !formData.is_public })}
              >
                <div style={{ ...styles.switchDot, left: formData.is_public ? '22px' : '4px' }} />
              </div>
            </div>

            <p style={styles.marketplaceSub}>
              {formData.is_public
                ? (t(LangKeys.service_marketplace_sub_active) || 'Visible in your online service store and local search')
                : (t(LangKeys.service_marketplace_sub_inactive) || 'Private service, only visible in your Khata/Billing')}
            </p>

            {formData.is_public && (
              <div style={styles.onlineFields}>
                {/* Photo Guidelines & Video Guide Card */}
                <PhotoGuidelinesAndVideoCard
                  hasPhoto={!!(formData.photo_url || formData.image_file)}
                  isService={true}
                />

                {/* Category Selector */}
                <div>
                  <label style={styles.label}>{t(LangKeys.item_category_lbl) || 'Service Category'}</label>
                  <div style={styles.categorySelectBtn} onClick={() => setShowCategoryPicker(true)}>
                    <div style={styles.categoryIconWrap}>{selectedCategoryInfo.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={styles.categoryTitle}>{selectedCategoryInfo.title}</div>
                      <div style={styles.categorySub}>{selectedCategoryInfo.subtitle}</div>
                    </div>
                    <button type="button" style={styles.changeBadge}>
                      {t(LangKeys.change_btn) || 'Change'} <ChevronDown size={14} />
                    </button>
                  </div>
                </div>

                {/* Optional Discount Price */}
                <div style={{ marginTop: '12px' }}>
                  <label style={styles.label}>
                    {t(LangKeys.service_discount_price_lbl) || 'Offer / Discount Price (Optional)'}
                  </label>
                  <div style={styles.priceInputRow}>
                    <span style={{ color: '#9333EA', fontWeight: 'bold' }}>₹</span>
                    <input
                      type="number"
                      style={styles.priceInput}
                      placeholder={t(LangKeys.service_discount_price_hint) || 'e.g. ₹450 (Customers will see discount)'}
                      value={formData.discount_price}
                      onChange={e => setFormData({ ...formData, discount_price: e.target.value })}
                    />
                  </div>
                </div>

                {/* Description & Rich SEO Formatting Toolbar */}
                <div style={{ marginTop: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={styles.label}>
                      {t(LangKeys.service_desc_lbl) || 'Service Description & Specialty (For SEO)'}
                    </label>
                    <span style={styles.seoBadge}>
                      <Sparkles size={11} color="#9333EA" /> {t(LangKeys.market_seo_badge) || 'Google & Search SEO'}
                    </span>
                  </div>
                  <DescriptionToolbarTextarea
                    value={formData.description}
                    onChange={val => setFormData({ ...formData, description: val })}
                    itemName={formData.name}
                    isService={true}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button style={styles.saveBtn} onClick={handleSave} disabled={loading}>
            {loading ? (
              <div className="spinner-small" />
            ) : (
              <>
                <Zap size={18} /> {item ? t(LangKeys.save_changes) : t(LangKeys.add_service)}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Pickers */}
      <UnitPickerModal
        isOpen={showUnitPicker}
        onClose={() => setShowUnitPicker(false)}
        onSelect={(u) => setFormData({ ...formData, unit: u })}
      />
      <GstPickerModal
        isOpen={showGstPicker}
        onClose={() => setShowGstPicker(false)}
        onSelect={(g) => setFormData({ ...formData, gst_rate: g })}
        selectedValue={formData.gst_rate}
      />
      <CategoryPickerModal
        isOpen={showCategoryPicker}
        onClose={() => setShowCategoryPicker(false)}
        selectedId={formData.category}
        onSelect={(catId) => setFormData({ ...formData, category: catId })}
        isService={true}
      />
      <ImageCropperModal
        isOpen={showCropper}
        imageSrc={rawImageSrc}
        onClose={() => setShowCropper(false)}
        onCropComplete={handleCropComplete}
        aspectRatio={1}
        title={t(LangKeys.crop_service_photo)}
      />

      <style>{`
        .spinner-small {
          width: 20px;
          height: 20px;
          border: 2px solid #FFF;
          border-top: 2px solid transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },
  modal: {
    width: '100%',
    maxWidth: '520px',
    maxHeight: '95vh',
    backgroundColor: '#FFF',
    borderRadius: '24px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
  },
  header: {
    padding: '22px 24px',
    color: '#FFF',
    backgroundColor: '#9333EA'
  },
  headerTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    fontSize: '20px',
    fontWeight: '800',
    margin: 0
  },
  closeBtn: {
    border: 'none',
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: '#FFF',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  },
  body: {
    padding: '20px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    backgroundColor: '#F8FAFC'
  },
  sectionCard: {
    backgroundColor: '#FFF',
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid #E2E8F0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  marketplaceCard: {
    backgroundColor: '#FAF5FF',
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid #F3E8FF',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  marketplaceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  marketplaceTitle: {
    fontSize: '14px',
    fontWeight: '800',
    color: '#6B21A8'
  },
  marketplaceSub: {
    fontSize: '12px',
    color: '#64748B',
    margin: 0,
    lineHeight: '1.4'
  },
  onlineFields: {
    marginTop: '6px',
    paddingTop: '12px',
    borderTop: '1px solid #F3E8FF',
    display: 'flex',
    flexDirection: 'column'
  },
  categorySelectBtn: {
    marginTop: '6px',
    padding: '10px 12px',
    backgroundColor: '#FFF',
    borderRadius: '12px',
    border: '1px solid #CBD5E1',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer'
  },
  categoryIconWrap: {
    fontSize: '22px',
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    backgroundColor: '#FAF5FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #F3E8FF'
  },
  categoryTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#0F172A'
  },
  categorySub: {
    fontSize: '11px',
    color: '#64748B'
  },
  changeBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '5px 10px',
    borderRadius: '8px',
    backgroundColor: '#FAF5FF',
    border: '1px solid #E9D5FF',
    color: '#9333EA',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  seoBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 8px',
    backgroundColor: '#FAF5FF',
    borderRadius: '6px',
    border: '1px solid #F3E8FF',
    color: '#9333EA',
    fontSize: '10px',
    fontWeight: '700'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFF',
    fontSize: '13px',
    color: '#1E293B',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical'
  },
  row: {
    display: 'flex',
    gap: '16px'
  },
  inputGroup: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#64748B'
  },
  input: {
    height: '40px',
    border: 'none',
    borderBottom: '2px solid #F1F5F9',
    outline: 'none',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1E293B',
    backgroundColor: 'transparent'
  },
  photoBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer'
  },
  photoPreview: {
    width: '60px',
    height: '60px',
    borderRadius: '12px',
    backgroundColor: '#F1F5F9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #E2E8F0',
    overflow: 'hidden'
  },
  photoText: {
    fontSize: '11px',
    color: '#94A3B8',
    fontWeight: '600'
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  selectBox: {
    height: '40px',
    borderBottom: '2px solid #F1F5F9',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  priceInputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    borderBottom: '2px solid #F1F5F9',
    height: '40px'
  },
  priceInput: {
    flex: 1,
    height: '40px',
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    fontWeight: '700',
    backgroundColor: 'transparent'
  },
  taxToggle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '6px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#1E293B'
  },
  switch: {
    width: '44px',
    height: '24px',
    borderRadius: '20px',
    position: 'relative',
    cursor: 'pointer',
    transition: '0.3s'
  },
  switchDot: {
    width: '18px',
    height: '18px',
    backgroundColor: '#FFF',
    borderRadius: '50%',
    position: 'absolute',
    top: '3px',
    transition: '0.3s',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
  },
  gstToggleBtn: {
    border: 'none',
    background: 'none',
    color: '#9333EA',
    fontWeight: '800',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '8px',
    textAlign: 'center'
  },
  footer: {
    padding: '16px 20px',
    borderTop: '1px solid #F1F5F9',
    backgroundColor: '#FFF'
  },
  saveBtn: {
    width: '100%',
    height: '50px',
    backgroundColor: '#9333EA',
    border: 'none',
    color: '#FFF',
    borderRadius: '14px',
    fontWeight: '800',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(147, 51, 234, 0.35)',
    fontSize: '15px'
  }
};

export default AddServiceModal;
